import { useEffect, useRef } from 'react';
import { isMobileDevice } from '@/utils/utils';
import beTwMerge from '@/utils/beTwMerge';
import ResponsiveImage, { ResponsiveImageSet } from '@/utils/ResponsiveImage';
import Button from '@/components/homepage/Button/Button';
import { ColorKeys } from '@/styles/colors';
import useMediaQuery from '@/hooks/useMediaQuery';
import { subHeadersStyles } from '@/consts/builder';
import { isRichTextEmpty } from '@/utils/builderUtils';
import DropdownButton from '../homepage/DropdownButton';
import RichText from '../RichText/RichText';

export type Action = {
  label: string;
  type?: 'link' | 'primary' | 'secondary';
  color?: ColorKeys;
  onClick?: () => void;
  href?: string;
  actions?: Action[];
  dropdown?: boolean;
};

interface ISingleImageTileProps {
  imgSrc?: ResponsiveImageSet;
  imgHref?: string;
  videoSrc?: string;
  mobileVideoSrc?: string;
  videoTitle?: string;
  imgAlt: string;
  textOnImage?: {
    textOnImageStyles: 'h1' | 'h2';
    textOnImageTag: 'h1' | 'h2';
    textOnImageText: string;
  };
  items?: {
    heading: string;
    headingTag: 'h2' | 'h3';
    paragraph?: string;
    actions: Action[];
  }[];
  isSetMaxWidth: boolean;
  isTextCenter: boolean;
  className?: string;
  wistiaVideo?: boolean;
  desktopVideoSource?: string;
  mobileVideoSource?: string;
  controls?: boolean;
  loop?: boolean;
  autoplay?: boolean;
}

const colorMap = {
  dark: 'dark-green',
  light: 'white',
};

export default function SingleImageTile({
  imgSrc,
  imgHref,
  videoSrc,
  mobileVideoSrc,
  imgAlt,
  textOnImage,
  items = [],
  isSetMaxWidth,
  isTextCenter,
  className,
  wistiaVideo,
  desktopVideoSource,
  mobileVideoSource,
  controls,
  loop,
  autoplay,
  videoTitle = 'Wistia video',
}: ISingleImageTileProps) {
  const buttonRefs = useRef<any[]>([]);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const { textOnImageStyles, textOnImageTag, textOnImageText } = textOnImage || {
    textOnImageStyles: 'h1',
    textOnImageTag: 'h2',
    textOnImageText: '',
  };
  const TextOnImageTag = textOnImageTag || 'h2';
  const isOnlyOneItem = items.length === 1;
  useEffect(() => {
    if (!isOnlyOneItem || !buttonRefs || buttonRefs.current.length < 2) return;
    const resizeWidth = () => {
      buttonRefs.current.forEach(button => {
        button.style.width = 'auto';
      });
      if (window.innerWidth < 1024) return;

      let maxWidth = buttonRefs.current[0].offsetWidth;
      buttonRefs.current.forEach(button => {
        if (button.offsetWidth > maxWidth) {
          maxWidth = button.offsetWidth;
        }
      });
      buttonRefs.current.forEach(button => {
        button.style.width = `${maxWidth + 1}px`;
      });
    };
    resizeWidth();
    window.addEventListener('resize', resizeWidth);
    return () => {
      window.removeEventListener('resize', resizeWidth);
    };
  }, [buttonRefs, isOnlyOneItem]);

  const moreDescriptiveAriaLabel = (
    headerTitle: string | undefined,
    href: string | undefined,
    label: string
  ): string => {
    if (label && (label.toLowerCase().trim() === 'shop now' || label.toLowerCase().trim() === 'learn more')) {
      if (headerTitle) {
        return `${headerTitle} - ${label}`;
      }

      return `${label} on ${href?.replace(/\/$/, '').split('/').pop()?.replace(/-/g, ' ')} page`;
    }
    return label || '';
  };
  const mobileThumbnail = imgSrc ? imgSrc[0]['src'] : '/bee2-ui/img/homepage/Capture-Collection-Mobile.png';
  const desktopThumbnail = imgSrc ? imgSrc[1]['src'] : '/bee2-ui/img/homepage/Capture-Collection-Desktop.jpg';

  useEffect(() => {
    // TODO: normalize this into a single place, see HeroVideo.tsx
    const WISTIA_MAIN_SCRIPT_ID = 'wistia-player-script';

    if (!document.querySelector(`#${WISTIA_MAIN_SCRIPT_ID}`)) {
      // include this ONCE for all instances
      const wistiaScript = document.createElement('script');
      wistiaScript.id = WISTIA_MAIN_SCRIPT_ID;
      wistiaScript.type = 'text/javascript';
      wistiaScript.src = '//fast.wistia.net/assets/external/E-v1.js';
      wistiaScript.async = true;
      document.head.appendChild(wistiaScript);
    }

    return () => {};
  }, []);

  const media =
    videoSrc || desktopVideoSource ? (
      <div
        style={{ backgroundImage: isMobileDevice() ? `url(${mobileThumbnail})` : `url(${desktopThumbnail})` }}
        className="tw-max-w-full tw-w-full tw-object-fill tw-aspect-[751/870] md:tw-aspect-[172/75] tw-bg-cover tw-overflow-hidden"
      >
        {wistiaVideo ? (
          <>
            <iframe
              src={videoSrc}
              title={videoTitle}
              scrolling="no"
              className="wistia_embed tw-hidden lg:tw-block tw-w-full tw-h-full tw-border-0 tw-overflow-hidden"
              allowFullScreen={false}
              width="172"
              height="75"
            />

            <iframe
              src={mobileVideoSrc}
              title={videoTitle}
              scrolling="no"
              className="wistia_embed tw-block lg:tw-hidden tw-w-full tw-h-full tw-border-0 tw-overflow-hidden"
              allowFullScreen={false}
              width="751"
              height="870"
            />
          </>
        ) : (
          <video
            className="tw-w-full tw-h-full tw-aspect-[169/180] tw-object-cover tw-mx-auto"
            muted
            preload="auto"
            controls={controls}
            loop={loop}
            autoPlay={autoplay}
            playsInline
            controlsList="nofullscreen nodownload noremoteplayback noplaybackrate foobar"
            disablePictureInPicture
            poster={isMobileDevice() ? mobileThumbnail : desktopThumbnail}
          >
            <source media="(max-width: 767px)" src={mobileVideoSource} type="video/mp4" />
            <source media="(min-width: 768px)" src={desktopVideoSource} type="video/mp4" />

            <track kind="captions" />
          </video>
        )}
      </div>
    ) : (
      <picture>
        <ResponsiveImage
          loading="lazy"
          sources={imgSrc!}
          alt={imgAlt || items[0]?.heading || textOnImageText || ''}
          className="tw-max-w-full tw-w-full tw-h-full tw-object-fill"
        />
      </picture>
    );

  return (
    <div
      className={`tw-mx-4 lg:tw-mx-8 ${isSetMaxWidth ? 'min-[1504px]:tw-mx-auto min-[1504px]:tw-w-[1440px] tw-max-w-[1440px]' : ''}  ${className}`}
    >
      <div className="tw-gap-x-4 tw-grid tw-grid-cols-2 md:tw-grid-cols-6 lg:tw-grid-cols-12">
        <div className="tw-col-span-full tw-inline-block tw-relative">
          {media}
          {imgHref && (
            <a
              href={imgHref}
              aria-label={imgHref}
              className={beTwMerge(
                'tw-absolute tw-w-full tw-left-0 tw-top-0',
                controls ? 'tw-h-[85%] lg:tw-h-[90%]' : 'tw-h-full'
              )}
            />
          )}
          {textOnImageText && (
            <div className="tw-absolute tw-w-full tw-h-full tw-flex tw-left-0 tw-top-0 tw-justify-center tw-items-center tw-text-white tw-px-12">
              <TextOnImageTag className={`tw-mb-0 tw-text-center ${subHeadersStyles[textOnImageStyles || 'h1']}`}>
                {textOnImageText}
              </TextOnImageTag>
            </div>
          )}
        </div>
        {items.length > 0 && items.length < 5 && (
          <div
            className={`tw-col-span-full tw-flex tw-justify-center tw-items-start tw-flex-col tw-gap-4 tw-mt-4 large:tw-flex-row large:tw-gap-8 ${isOnlyOneItem ? 'large:tw-mt-4' : 'large:tw-mt-8'}`}
          >
            {items.slice(0, 4).map((item, index) => {
              const isLastItem = index === items.length - 1;
              const { heading, headingTag, paragraph, actions } = item;
              const HeadingTag = headingTag || 'h3';
              const renderHeading = () => {
                switch (true) {
                  case !heading:
                    return null;
                  case isOnlyOneItem:
                    return <h2 className="first:tw-mt-0 tw-mb-0">{heading}</h2>;
                  default:
                    return (
                      <HeadingTag className={beTwMerge('tw-mt-0 tw-mb-0', subHeadersStyles[HeadingTag])}>
                        {heading}
                      </HeadingTag>
                    );
                }
              };
              const renderDescription = () => {
                if (!paragraph || isRichTextEmpty(paragraph)) return null;
                return (
                  <RichText
                    className={`tw-mt-2 ${isOnlyOneItem ? '[&_p]:!tw-text-[16px]' : '[&_p]:!tw-text-[14px]'}`}
                    content={paragraph}
                  />
                );
              };

              const renderCta = () => {
                if (actions === undefined || actions?.length === 0) return null;
                return actions.slice(0, 4)?.map((action, i) => {
                  const { label, color, type, href } = action;
                  const isLink = type === 'link';
                  const isFirstItem = i === 0;
                  const dropdownButtonClass = beTwMerge(
                    'tw-max-w-none tw-font-avenirmedium tw-col-span-1 [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap tw-text-ellipsis tw-mt-0 large:tw-mt-6 tw-h-[48px] large:tw-h-[44px]',
                    isFirstItem && 'tw-mt-6',
                    isLink && isFirstItem && 'tw-mt-2'
                  );
                  const buttonClass = beTwMerge(
                    'tw-max-w-none tw-font-avenirmedium tw-col-span-1 !tw-flex tw-items-center tw-justify-center !tw-leading-4 tw-py-3 tw-px-4 tw-text-center tw-h-[44px] tw-whitespace-nowrap tw-mt-0 large:tw-mt-6 tw-h-[48px] large:tw-h-[44px]',
                    isLink && 'tw-text-[14px] tw-p-0 tw-h-auto',
                    isLink && !isTextCenter && 'tw-justify-start',
                    isFirstItem && 'tw-mt-6',
                    isLink && isFirstItem && 'tw-mt-2'
                  );
                  if (action.actions && action.dropdown && type !== 'link') {
                    const { actions: subActions } = action;
                    return (
                      <DropdownButton
                        key={`singleImageTiles-tileCTA-${i}`}
                        className={dropdownButtonClass}
                        label={label}
                        options={subActions}
                        color={color ? colorMap[color] : 'dark-green'}
                        btnRef={(el: any) => {
                          buttonRefs.current[i] = el;
                        }}
                        type={type || 'secondary'}
                      />
                    );
                  }
                  return (
                    <Button
                      key={`singleImageTiles-tileCTA-${i}`}
                      className={buttonClass}
                      label={label}
                      ariaLabel={moreDescriptiveAriaLabel(heading, href, label)}
                      color={color ? colorMap[color] : 'dark-green'}
                      type={type || 'secondary'}
                      ref={(el: any) => {
                        buttonRefs.current[i] = el;
                      }}
                      href={href}
                    />
                  );
                });
              };

              const lineDiv = isDesktop ? (
                <div className="tw-w-[1px] tw-h-full tw-bg-black"> </div>
              ) : (
                <div className="tw-h-[1px] tw-w-full tw-bg-[#D9D9D9]"> </div>
              );

              return (
                <>
                  <div
                    className={beTwMerge(
                      'tw-w-full tw-flex tw-flex-col tw-items-start',
                      isTextCenter && 'tw-items-center tw-text-center'
                    )}
                  >
                    {renderHeading()}
                    {renderDescription()}
                    <div
                      className={`tw-w-full tw-flex tw-flex-wrap ${isTextCenter ? 'tw-justify-center' : ''} tw-flex-col tw-gap-2 large:tw-flex-row`}
                    >
                      {renderCta()}
                    </div>
                  </div>
                  {!isLastItem && lineDiv}
                </>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
